/**
 * Resource Definitions — application-level data resources.
 *
 * Centralizes cache keys, fetchers, and cache policies.
 * Pages import these and pass to createQuery().
 *
 * Cache policies from TTA v2 spec:
 *   Courses:     staleTime = 5 min,  gcTime = 30 min
 *   Course:      staleTime = 10 min, gcTime = 60 min
 *   Lecture:     staleTime = session (Infinity), gcTime = session (Infinity)
 *   Books:       staleTime = 10 min, gcTime = 60 min
 *   Book:        staleTime = 10 min, gcTime = 60 min
 *   Library:     staleTime = 1 min,  gcTime = 15 min
 *   Admin Stats: staleTime = 30 sec, gcTime = 5 min
 */

import {
  get_breakdown,
  list_courses,
  get_course,
  get_course_question_frequency,
  get_module_question_frequency,
  list_lectures_for_course,
  get_lecture,
  list_books,
  get_book,
  list_public_courses,
  get_public_course,
  list_public_books,
  get_public_book,
  list_my_subscriptions,
  get_admin_stats,
  get_business_analytics,
  get_business_growth,
  get_user_growth,
  get_revenue_growth,
  get_subscription_growth,
  get_learning_analytics,
  get_video_analytics,
  get_activity_rolling,
  get_student_health,
  get_activity_heatmap,
  get_recent_activity,
  get_session_duration_distribution,
  get_revenue_by_faculty,
  get_top_selling_content,
  get_quiz_accuracy_trend,
  get_quiz_difficulty_distribution,
  get_quiz_topics,
  get_learning_time_to_complete,
  get_engagement_feature_usage,
  get_engagement_journey,
  get_learning_time_series,
  get_revenue_refunds,
  get_users_by_signup_source,
  get_events_by_device,
  get_events_by_browser,
  get_events_by_os,
  // Progress (TTA v3)
  get_lecture_progress,
  get_course_progress,
  get_my_progress,
  // Reading (TTA v2)
  list_reading_modules,
  get_reading_module,
  get_reading_submodule,
  get_reading_question,
  search_reading_questions,
  list_bookmarks,
  list_all_highlights,
  list_reading_progress,
  get_reading_progress,
  get_reading_lecture,
  list_public_lectures,
  // Quiz Sessions (TTA v3)
  create_quiz_session,
  list_quiz_sessions,
  get_quiz_session,
  get_active_session,
  delete_quiz_session,
  pause_session,
  resume_session,
  finish_session,
  save_session_answer,
  get_session_answers,
  get_session_summary,
  get_session_review,
  // Dashboard & Statistics (TTA v4)
  get_dashboard,
  get_my_activity_calendar,
  get_question_statistics,
  get_question_community_stats,
  get_community_comparison,
  // Playlists (TTA v5)
  list_playlists,
  get_playlist,
  list_playlist_items,
  // Smart Collections (TTA v5)
  list_collections,
  get_collection,
  get_questions_batch,
  // Admin Reports (TTA v5)
  admin_list_reports,
  admin_report_statistics,
} from '$lib/api-calls.js'

// ── Helpers ──

/** Unwrap api_request response → response_data */
const unwrap = (fn) => (...args) => fn(...args).then(r => r.response_data)

/** Unwrap and default to empty array */
const unwrapList = (fn) => (...args) =>
  fn(...args).then(r => r.response_data || [])

// ── Resource Definitions ──

/**
 * The catalog, as read by a visitor with no session.
 *
 * Same pages, narrower payload: the public endpoints carry no subscription
 * state, no progress and no media (see main/schemas/public_catalog.py). Their
 * own cache keys, so a signed-out response can never be handed to a signed-in
 * reader after login, or the reverse.
 */
export const publicCoursesResource = {
  key: ['public-courses'],
  fetcher: unwrapList(list_public_courses),
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
}

export const publicBooksResource = {
  key: ['public-books'],
  fetcher: unwrapList(list_public_books),
  staleTime: 10 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
}

export function publicCourseResource(id) {
  return {
    key: ['public-course', id],
    fetcher: () => unwrap(get_public_course)(id),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

export function publicBookResource(id) {
  return {
    key: ['public-book', id],
    fetcher: () => unwrap(get_public_book)(id),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/** @type {import('./types.js').ResourceDef} */
export const coursesResource = {
  key: ['courses'],
  fetcher: unwrapList(list_courses),
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
}

/**
 * Courses filtered by study year (URL order).
 * @param {number|string|null} year
 */
export function coursesForYearResource(year) {
  return {
    key: ['courses', { year }],
    fetcher: () => unwrapList(list_courses)(year),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

/**
 * Get course resource definition.
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function courseResource(id) {
  return {
    key: ['course', id],
    fetcher: () => unwrap(get_course)(id),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/**
 * Course lectures ranked by question frequency (most tested first).
 * @param {number|string} courseId
 * @returns {import('./types.js').ResourceDef}
 */
export function courseQuestionFrequencyResource(courseId) {
  return {
    key: ['course-question-frequency', courseId],
    fetcher: () => unwrap(get_course_question_frequency)(courseId),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/**
 * A module's lectures ranked by question frequency (most tested first).
 * @param {number|string} moduleId
 * @returns {import('./types.js').ResourceDef}
 */
export function moduleQuestionFrequencyResource(moduleId) {
  return {
    key: ['module-question-frequency', moduleId],
    fetcher: () => unwrap(get_module_question_frequency)(moduleId),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/**
 * Get lectures-for-course resource.
 * @param {number|string} courseId
 * @returns {import('./types.js').ResourceDef}
 */
export function lecturesForCourseResource(courseId) {
  return {
    key: ['lectures', 'course', courseId],
    fetcher: unwrapList(() => list_lectures_for_course(courseId)),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/**
 * Get lecture resource.
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function lectureResource(id) {
  return {
    key: ['lecture', id],
    fetcher: () => get_lecture(id).then(r => r.response_data),
    staleTime: Infinity, // session — content changes rarely
    gcTime: Infinity,
  }
}

/** @type {import('./types.js').ResourceDef} */
export const booksResource = {
  key: ['books'],
  fetcher: unwrapList(list_books),
  staleTime: 10 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
}

/**
 * Books filtered by study year (URL order).
 * @param {number|string|null} year
 */
export function booksForYearResource(year) {
  return {
    key: ['books', { year }],
    fetcher: () => unwrapList(list_books)(year),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/**
 * Get book resource definition.
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function bookResource(id) {
  return {
    key: ['book', id],
    fetcher: () => unwrap(get_book)(id),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/** @type {import('./types.js').ResourceDef} */
export const libraryResource = {
  key: ['library'],
  fetcher: unwrapList(list_my_subscriptions),
  staleTime: 1 * 60 * 1000,
  gcTime: 15 * 60 * 1000,
}

/** @type {import('./types.js').ResourceDef} */
export const adminStatsResource = {
  key: ['admin-stats'],
  fetcher: () => get_admin_stats(),
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
}

// ── Analytics Resources ────────────────────────────────────────────────────

/** @type {import('./types.js').ResourceDef} */
export const businessAnalyticsResource = {
  key: ['business-analytics'],
  fetcher: () => get_business_analytics().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
}

/**
 * @param {{ period: string, granularity: string | null, startDate: string | null, endDate: string | null, comparison: boolean }} params
 * @returns {import('./types.js').ResourceDef}
 */
export function businessGrowthResource({ period, granularity, startDate, endDate, comparison }) {
  return {
    key: ['business-growth', { period, granularity, startDate, endDate, comparison }],
    fetcher: () => get_business_growth({ period, granularity, start_date: startDate, end_date: endDate, comparison })
      .then(r => r.response_data),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  }
}

// Per-series growth resources — one request per chart so each renders
// independently and users see partial data as soon as any endpoint replies.
function _seriesResource(name, fetcher) {
  return ({ period, granularity, startDate, endDate, comparison }) => ({
    key: [`growth-${name}`, { period, granularity, startDate, endDate, comparison }],
    fetcher: () => fetcher({ period, granularity, start_date: startDate, end_date: endDate, comparison })
      .then(r => r.response_data),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export const userGrowthResource = _seriesResource('users', get_user_growth)
export const revenueGrowthResource = _seriesResource('revenue', get_revenue_growth)
export const subscriptionGrowthResource = _seriesResource('subscriptions', get_subscription_growth)

/** @type {import('./types.js').ResourceDef} */
export const activityRollingResource = {
  key: ['activity-rolling', 30],
  fetcher: () => get_activity_rolling({ days: 30 }).then(r => r.response_data),
  staleTime: 60 * 1000,
  gcTime: 5 * 60 * 1000,
}

/** @type {import('./types.js').ResourceDef} */
export const studentHealthResource = {
  key: ['student-health'],
  fetcher: () => get_student_health().then(r => r.response_data),
  staleTime: 60 * 1000,
  gcTime: 5 * 60 * 1000,
}

// ── Extended analytics (Phase 2 endpoints) ────────────────────────────────

const _ANALYTICS_STALE = 60 * 1000
const _ANALYTICS_GC = 5 * 60 * 1000

export function activityHeatmapResource({ days = 30, startDate = null, endDate = null } = {}) {
  const custom = !!(startDate && endDate)
  return {
    key: custom ? ['activity-heatmap', 'range', startDate, endDate] : ['activity-heatmap', days],
    fetcher: () => get_activity_heatmap(custom ? { startDate, endDate } : { days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function recentActivityResource(limit = 20) {
  return {
    key: ['activity-recent', limit],
    fetcher: () => get_recent_activity({ limit }).then(r => r.response_data),
    staleTime: 30 * 1000,
    gcTime: _ANALYTICS_GC,
  }
}

export const sessionDurationDistributionResource = {
  key: ['sessions-duration-distribution'],
  fetcher: () => get_session_duration_distribution().then(r => r.response_data),
  staleTime: _ANALYTICS_STALE,
  gcTime: _ANALYTICS_GC,
}

export function revenueByFacultyResource(days = 30) {
  return {
    key: ['revenue-by-faculty', days],
    fetcher: () => get_revenue_by_faculty({ days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function topSellingContentResource({ contentType = null, limit = 10 } = {}) {
  return {
    key: ['revenue-top-content', contentType, limit],
    fetcher: () => get_top_selling_content({ content_type: contentType, limit }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function quizAccuracyTrendResource({ period = '30d', granularity = null, startDate = null, endDate = null } = {}) {
  return {
    key: ['quiz-accuracy-trend', { period, granularity, startDate, endDate }],
    fetcher: () => get_quiz_accuracy_trend({ period, granularity, start_date: startDate, end_date: endDate })
      .then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export const quizDifficultyDistributionResource = {
  key: ['quiz-difficulty'],
  fetcher: () => get_quiz_difficulty_distribution().then(r => r.response_data),
  staleTime: _ANALYTICS_STALE,
  gcTime: _ANALYTICS_GC,
}

export function quizTopicsResource({ order = 'weakest', limit = 10, minAttempts = 20 } = {}) {
  return {
    key: ['quiz-topics', order, limit, minAttempts],
    fetcher: () => get_quiz_topics({ order, limit, min_attempts: minAttempts }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export const learningTimeToCompleteResource = {
  key: ['learning-time-to-complete'],
  fetcher: () => get_learning_time_to_complete().then(r => r.response_data),
  staleTime: _ANALYTICS_STALE,
  gcTime: _ANALYTICS_GC,
}

export function engagementFeatureUsageResource(days = 30) {
  return {
    key: ['engagement-feature-usage', days],
    fetcher: () => get_engagement_feature_usage({ days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function engagementJourneyResource({ limit = 10, days = 30 } = {}) {
  return {
    key: ['engagement-journey', limit, days],
    fetcher: () => get_engagement_journey({ limit, days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function learningTimeSeriesResource({ period = '30d', granularity = null, startDate = null, endDate = null } = {}) {
  return {
    key: ['learning-time-series', { period, granularity, startDate, endDate }],
    fetcher: () => get_learning_time_series({ period, granularity, start_date: startDate, end_date: endDate })
      .then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function revenueRefundsResource(days = 30) {
  return {
    key: ['revenue-refunds', days],
    fetcher: () => get_revenue_refunds({ days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export const usersBySignupSourceResource = {
  key: ['users-by-signup-source'],
  fetcher: () => get_users_by_signup_source().then(r => r.response_data),
  staleTime: _ANALYTICS_STALE,
  gcTime: _ANALYTICS_GC,
}

export function eventsByDeviceResource(days = 30) {
  return {
    key: ['events-by-device', days],
    fetcher: () => get_events_by_device({ days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function eventsByBrowserResource(days = 30) {
  return {
    key: ['events-by-browser', days],
    fetcher: () => get_events_by_browser({ days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

export function eventsByOsResource(days = 30) {
  return {
    key: ['events-by-os', days],
    fetcher: () => get_events_by_os({ days }).then(r => r.response_data),
    staleTime: _ANALYTICS_STALE,
    gcTime: _ANALYTICS_GC,
  }
}

/** @type {import('./types.js').ResourceDef} */
export const learningAnalyticsResource = {
  key: ['learning-analytics'],
  fetcher: () => get_learning_analytics().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
}

/** @type {import('./types.js').ResourceDef} */
export const videoAnalyticsResource = {
  key: ['video-analytics'],
  fetcher: () => get_video_analytics().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
}

/**
 * Distribution / breakdown resource.
 *
 * `kind` is the URL suffix under `/analytics/`, e.g. `users/breakdown/study-year`.
 * `params` is an object of extra query params (`days`, `limit`).
 *
 * Every deep-dive donut and dashboard mini-chart shares this resource so
 * navigating from dashboard → deep-dive is instant (cache hit).
 */
export function breakdownResource(kind, params = {}) {
  return {
    key: ['breakdown', kind, params],
    fetcher: () => get_breakdown(kind, params).then(r => r.response_data || []),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  }
}

// ── Progress Resources (TTA v3) ────────────────────────────────────────────

/**
 * @param {number|string} lectureId
 * @returns {import('./types.js').ResourceDef}
 */
export function lectureProgressResource(lectureId) {
  return {
    key: ['lecture-progress', lectureId],
    fetcher: () => unwrap(get_lecture_progress)(lectureId),
    staleTime: 0,         // always fresh (resume position)
    gcTime: 5 * 60 * 1000,
  }
}

/**
 * @param {number|string} courseId
 * @returns {import('./types.js').ResourceDef}
 */
export function courseProgressResource(courseId) {
  return {
    key: ['course-progress', courseId],
    fetcher: () => unwrap(get_course_progress)(courseId),
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  }
}

/** @type {import('./types.js').ResourceDef} */
export const myProgressResource = {
  key: ['my-progress'],
  fetcher: () => unwrap(get_my_progress)(),
  staleTime: 10 * 1000,
  gcTime: 5 * 60 * 1000,
}

// ── Reading Resources (TTA v2) ──────────────────────────────────────────────

/**
 * @param {object} [filters]
 * @returns {import('./types.js').ResourceDef}
 */
export function readingModulesResource(filters = {}) {
  return {
    key: ['reading-modules', filters],
    fetcher: () => list_reading_modules(filters).then(r => r.response_data),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function readingModuleResource(id) {
  return {
    key: ['reading-module', id],
    fetcher: () => get_reading_module(id).then(r => r.response_data),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function readingSubmoduleResource(id) {
  return {
    key: ['reading-submodule', id],
    fetcher: () => get_reading_submodule(id).then(r => r.response_data),
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  }
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function readingQuestionResource(id) {
  return {
    key: ['reading-question', id],
    fetcher: () => get_reading_question(id).then(r => r.response_data),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  }
}

/**
 * @param {object} params
 * @returns {import('./types.js').ResourceDef}
 */
export function readingSearchResource(params) {
  return {
    key: ['reading-search', params],
    fetcher: () => search_reading_questions(params).then(r => r.response_data),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  }
}

/** @type {import('./types.js').ResourceDef} */
export const bookmarksResource = {
  key: ['reading-bookmarks'],
  fetcher: () => list_bookmarks().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 10 * 60 * 1000,
}

/** @type {import('./types.js').ResourceDef} */
export const highlightsResource = {
  key: ['reading-highlights'],
  fetcher: () => list_all_highlights().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 10 * 60 * 1000,
}

/** @type {import('./types.js').ResourceDef} */
export const readingProgressListResource = {
  key: ['reading-progress'],
  fetcher: () => list_reading_progress().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 10 * 60 * 1000,
}

/**
 * @param {number|string} lectureId
 * @returns {import('./types.js').ResourceDef}
 */
export function readingProgressResource(lectureId) {
  return {
    key: ['reading-progress', lectureId],
    fetcher: () => get_reading_progress(lectureId).then(r => r.response_data),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  }
}

// ── Reading Lectures (TTA v6) ───────────────────────────────────────────

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function readingLectureResource(id) {
  return {
    key: ['reading-lecture', id],
    fetcher: () => get_reading_lecture(id).then(r => r.response_data),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

/**
 * @param {number|string} submoduleId
 * @returns {import('./types.js').ResourceDef}
 */
export function publicLecturesResource(submoduleId) {
  return {
    key: ['public-lectures', submoduleId],
    fetcher: () => list_public_lectures(submoduleId).then(r => r.response_data),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

// ── Quiz Sessions (TTA v3) ────────────────────────────────────────────────

/** @type {import('./types.js').ResourceDef} */
export const sessionsResource = {
  key: ['quiz-sessions'],
  fetcher: () => list_quiz_sessions().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function sessionResource(id) {
  return {
    key: ['quiz-session', id],
    fetcher: () => get_quiz_session(id).then(r => r.response_data),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  }
}

/** @type {import('./types.js').ResourceDef} */
export const activeSessionResource = {
  key: ['active-session'],
  fetcher: () => get_active_session().then(r => r.response_data),
  staleTime: 0,
  gcTime: 0,
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function sessionAnswersResource(id) {
  return {
    key: ['session-answers', id],
    fetcher: () => get_session_answers(id).then(r => r.response_data),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  }
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function sessionSummaryResource(id) {
  return {
    key: ['session-summary', id],
    fetcher: () => get_session_summary(id).then(r => r.response_data),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function sessionReviewResource(id) {
  return {
    key: ['session-review', id],
    fetcher: () => get_session_review(id).then(r => r.response_data),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

// ── Student Dashboard (TTA v4) ──────────────────────────────────────────────

/** @type {import('./types.js').ResourceDef} */
export const dashboardResource = {
  key: ['dashboard'],
  fetcher: () => get_dashboard().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
}

/**
 * @param {number} [days=365]
 * @returns {import('./types.js').ResourceDef}
 */
export function activityCalendarResource(days = 365) {
  return {
    key: ['activity-calendar', days],
    fetcher: () => get_my_activity_calendar({ days }).then(r => r.response_data),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  }
}

/**
 * @param {number|string} questionId
 * @returns {import('./types.js').ResourceDef}
 */
export function questionStatisticsResource(questionId) {
  return {
    key: ['question-statistics', questionId],
    fetcher: () => get_question_statistics(questionId).then(r => r.response_data),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  }
}

/**
 * @param {number|string} questionId
 * @returns {import('./types.js').ResourceDef}
 */
export function questionCommunityResource(questionId) {
  return {
    key: ['question-community', questionId],
    fetcher: () => get_question_community_stats(questionId).then(r => r.response_data),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  }
}

/**
 * @param {number|string} sessionId
 * @returns {import('./types.js').ResourceDef}
 */
export function communityComparisonResource(sessionId) {
  return {
    key: ['community-comparison', sessionId],
    fetcher: () => get_community_comparison(sessionId).then(r => r.response_data),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  }
}

// ── Playlists (TTA v5) ──────────────────────────────────────────────────

/** @type {import('./types.js').ResourceDef} */
export const playlistsResource = {
  key: ['playlists'],
  fetcher: () => list_playlists().then(r => r.response_data),
  staleTime: 10 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
}

/**
 * @param {number|string} id
 * @returns {import('./types.js').ResourceDef}
 */
export function playlistResource(id) {
  return {
    key: ['playlist', id],
    fetcher: () => get_playlist(id).then(r => r.response_data),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

/**
 * @param {number|string} playlistId
 * @returns {import('./types.js').ResourceDef}
 */
export function playlistItemsResource(playlistId) {
  return {
    key: ['playlist-items', playlistId],
    fetcher: () => list_playlist_items(playlistId).then(r => r.response_data),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

// ── Smart Collections (TTA v5) ──────────────────────────────────────────

/** @type {import('./types.js').ResourceDef} */
export const collectionsResource = {
  key: ['collections'],
  fetcher: () => list_collections().then(r => r.response_data),
  staleTime: 5 * 60 * 1000,
  gcTime: 15 * 60 * 1000,
}

/**
 * @param {string} slug
 * @returns {import('./types.js').ResourceDef}
 */
export function collectionResource(slug) {
  return {
    key: ['collection', slug],
    fetcher: () => get_collection(slug).then(r => r.response_data),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  }
}

/**
 * @param {number[]} questionIds
 * @returns {import('./types.js').ResourceDef}
 */
export function questionsBatchResource(questionIds) {
  return {
    key: ['questions-batch', questionIds],
    fetcher: () => get_questions_batch(questionIds).then(r => r.response_data),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  }
}

// ── Admin Reports (TTA v5) ──────────────────────────────────────────────

/** @type {import('./types.js').ResourceDef} */
export const adminReportsResource = {
  key: ['admin-reports'],
  fetcher: () => admin_list_reports().then(r => r.response_data),
  staleTime: 0,
  gcTime: 0,
}

/** @type {import('./types.js').ResourceDef} */
export const adminReportStatsResource = {
  key: ['admin-report-stats'],
  fetcher: () => admin_report_statistics().then(r => r.response_data),
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
}
